/**
 * <s-welcome-offer> — WELCOME10 welcome popup + guard against unrequested codes.
 *
 * Popup
 * - Opens once the 21+ age gate has been confirmed and closed (never at the same time).
 *   If the gate is open it waits for it to close; if the visitor leaves (declines 21+) it never shows.
 * - Waits while any other dialog is open (free gift, Best Offers picker, Mystery offer, cart drawer).
 * - "Yes" applies data-code through the same mechanism as the Mystery offer:
 *   GET /discount/<code> (stores the code for this browser, so it carries to carts created later
 *   and to checkout) + Shopify.actions.updateCart({ discountCodes }) so the open carts re-render.
 *   Then a short confirmation toast closes itself after ~3s.
 * - "No", X, Escape or backdrop close: nothing is applied.
 *
 * Memory (localStorage key tb-welcome10-choice, JSON { status, at }):
 * - accepted → never shown again.
 * - declined → hidden for the rest of the session (sessionStorage) and for 7 days (data-snooze-days).
 *
 * Guard
 * - On every page load, if the cart carries the code but this browser never accepted it
 *   (popup Yes, manual "Discount code" field, or Mystery signup), the code is removed from the
 *   cart and the discount cookie is cleared. Other codes are kept.
 */
const CHOICE_KEY = 'tb-welcome10-choice';
const SESSION_DECLINED_KEY = 'tb-welcome10-declined-session';
const SESSION_REAPPLIED_KEY = 'tb-welcome10-reapplied';
const AGE_KEY = 'tb-age-verified';

function store(kind, action, key, value) {
  try {
    const s = kind === 'session' ? window.sessionStorage : window.localStorage;
    if (action === 'get') return s.getItem(key);
    if (action === 'set') s.setItem(key, value);
    if (action === 'remove') s.removeItem(key);
  } catch {
    /* storage unavailable */
  }
  return null;
}

function readChoice() {
  try {
    const parsed = JSON.parse(store('local', 'get', CHOICE_KEY) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function markWelcomeAccepted(source = 'popup') {
  store('local', 'set', CHOICE_KEY, JSON.stringify({ status: 'accepted', source, at: Date.now() }));
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function waitForActions(timeout = 6000) {
  const start = Date.now();
  while (!window.Shopify?.actions?.updateCart) {
    if (Date.now() - start > timeout) return null;
    await wait(150);
  }
  return window.Shopify.actions;
}

class SWelcomeOffer extends HTMLElement {
  #ac;
  #accepted = false;
  #toastTimer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.dialog = this.querySelector('dialog');
    this.toast = this.querySelector('[data-welcome-toast]');
    if (!this.dialog) return;

    this.addEventListener('click', (event) => this.#onClick(event), opts);
    this.dialog.addEventListener('close', () => this.#onClose(), opts);

    this.#start();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#toastTimer);
  }

  get code() {
    return (this.dataset.code || '').trim();
  }

  get #snoozeMs() {
    const days = Number(this.dataset.snoozeDays || 7);
    return Math.max(0, days) * 24 * 60 * 60 * 1000;
  }

  async #start() {
    const cart = await this.#fetchCart();
    const hasCode = cart ? this.#cartCodes(cart).some((c) => c.toUpperCase() === this.code.toUpperCase()) : false;
    const choice = readChoice();

    if (hasCode && choice?.status !== 'accepted') {
      await this.#removeCode(cart);
    } else if (!hasCode && choice?.status === 'accepted' && cart?.item_count > 0 && !store('session', 'get', SESSION_REAPPLIED_KEY)) {
      // Accepted earlier but the code fell off this cart: re-apply once per session.
      store('session', 'set', SESSION_REAPPLIED_KEY, '1');
      this.#apply({ quiet: true });
    }

    if (this.#shouldShow(choice, hasCode && choice?.status === 'accepted')) this.#openWhenClear();
  }

  #shouldShow(choice, alreadyApplied) {
    if (!this.code || alreadyApplied) return false;
    if (window.location.pathname.includes('/checkouts/')) return false;
    if (choice?.status === 'accepted') return false;
    if (store('session', 'get', SESSION_DECLINED_KEY)) return false;
    if (choice?.status === 'declined' && Date.now() - Number(choice.at || 0) < this.#snoozeMs) return false;
    return true;
  }

  async #openWhenClear() {
    // Up to ~10 minutes of waiting for the age gate / other popups, checked every 500ms.
    for (let i = 0; i < 1200; i++) {
      if (this.#ac.signal.aborted) return;
      if (this.#isClear()) {
        // Small pause so the age gate's closing transition finishes first.
        await wait(400);
        if (this.#isClear() && !this.dialog.open) {
          this.dialog.showModal();
          this.querySelector('[data-autofocus]')?.focus();
        }
        return;
      }
      await wait(500);
    }
  }

  #isClear() {
    const gate = document.getElementById('age-gate');
    if (gate) {
      if (store('local', 'get', AGE_KEY) !== '1') return false;
      if (gate.querySelector('dialog')?.open) return false;
    }
    const otherOpen = [...document.querySelectorAll('dialog[open]')].some((d) => d !== this.dialog);
    return !otherOpen;
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-welcome-yes]')) {
      this.#accepted = true;
      markWelcomeAccepted('popup');
      this.dialog.close();
      this.#apply({ quiet: false });
      return;
    }
    if (target.closest('[data-welcome-no]')) {
      this.dialog.close();
    }
  }

  #onClose() {
    if (this.#accepted) return;
    // No, X, Escape or backdrop: remember the decline, apply nothing.
    store('local', 'set', CHOICE_KEY, JSON.stringify({ status: 'declined', at: Date.now() }));
    store('session', 'set', SESSION_DECLINED_KEY, '1');
  }

  async #apply({ quiet }) {
    const code = this.code;
    if (!code) return;
    try {
      // Stores the code for this browser so carts created later and checkout receive it.
      await fetch(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`, { credentials: 'same-origin' });
    } catch {
      /* continue with the cart update */
    }
    if (!quiet) this.#showToast();
    const actions = await waitForActions();
    if (!actions) return;
    try {
      const cart = await this.#fetchCart();
      const others = cart ? this.#cartCodes(cart).filter((c) => c.toUpperCase() !== code.toUpperCase()) : [];
      await actions.updateCart({ discountCodes: [...others, code] }, { event: { context: 'cart' } });
    } catch {
      /* The browser still carries the code from /discount; it applies when products are added. */
    }
  }

  async #removeCode(cart) {
    const code = this.code.toUpperCase();
    const remaining = this.#cartCodes(cart).filter((c) => c.toUpperCase() !== code);
    this.#clearDiscountCookie();
    const actions = await waitForActions();
    try {
      if (actions) {
        await actions.updateCart({ discountCodes: remaining }, { event: { context: 'cart' } });
      } else {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: remaining.join(',') }),
        });
        window.location.reload();
      }
    } catch {
      /* leave the cart as is */
    }
  }

  #clearDiscountCookie() {
    const host = window.location.hostname;
    const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
    for (const domain of domains) {
      document.cookie = `discount_code=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }

  async #fetchCart() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch {
      return null;
    }
  }

  #cartCodes(cart) {
    const codes = new Set();
    for (const entry of cart.discount_codes || []) if (entry?.code) codes.add(entry.code);
    for (const app of cart.cart_level_discount_applications || []) {
      if (app?.type === 'discount_code' && app.title) codes.add(app.title);
    }
    for (const item of cart.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        const app = alloc?.discount_application;
        if (app?.type === 'discount_code' && app.title) codes.add(app.title);
      }
    }
    return [...codes];
  }

  #showToast() {
    if (!this.toast) return;
    this.toast.hidden = false;
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => {
      this.toast.hidden = true;
    }, 3000);
  }
}

if (!customElements.get('s-welcome-offer')) {
  customElements.define('s-welcome-offer', SWelcomeOffer);
}
